import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, Search, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { DateField } from "@/components/DateField";
import { KarigarReportDialog } from "@/components/KarigarReportDialog";
import api, { fmt, fmtDate } from "@/lib/api";

const STATUSES = ["New", "With Karigar", "Ready", "In Shop", "Delivered", "Cancelled"];
const isoToday = () => new Date().toLocaleDateString("en-CA");
const isoTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString("en-CA"); };

export const priorityOf = (p) => {
  if (["Delivered", "Cancelled"].includes(p.status) || !p.delivery_date) return { label: "Normal", cls: "text-muted-foreground" };
  const diff = Math.round((new Date(p.delivery_date) - new Date(isoToday())) / 86400000);
  if (diff < 0) return { label: "Overdue", cls: "text-red-500 font-medium" };
  if (diff <= 2) return { label: "Urgent", cls: "text-amber-500 font-medium" };
  return { label: "Normal", cls: "text-muted-foreground" };
};

export default function Products() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState("All");
  const [karigar, setKarigar] = useState("All");
  const [status, setStatus] = useState("All");
  const [category, setCategory] = useState("All");
  const [delivery, setDelivery] = useState("all");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const { data: products, isLoading } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const uniq = (key) => [...new Set((products || []).map((p) => p[key]).filter(Boolean))].sort();
  const hasFilters = q || customer !== "All" || karigar !== "All" || status !== "All" || category !== "All" || delivery !== "all";
  const clearFilters = () => { setQ(""); setCustomer("All"); setKarigar("All"); setStatus("All"); setCategory("All"); setDelivery("all"); setDeliveryDate(""); };

  const filtered = (products || []).filter((p) => {
    if (customer !== "All" && p.customer_name !== customer) return false;
    if (karigar !== "All" && p.karigar_name !== karigar) return false;
    if (status !== "All" && p.status !== status) return false;
    if (category !== "All" && p.category !== category) return false;
    const active = !["Delivered", "Cancelled"].includes(p.status);
    if (delivery === "today" && !(p.delivery_date === isoToday() && active)) return false;
    if (delivery === "tomorrow" && !(p.delivery_date === isoTomorrow() && active)) return false;
    if (delivery === "overdue" && !(p.delivery_date && p.delivery_date < isoToday() && active)) return false;
    if (delivery === "custom" && deliveryDate && p.delivery_date !== deliveryDate) return false;
    if (q) {
      const hay = [p.name, p.category, p.order_number, p.customer_name, p.customer_phone, p.karigar_name, p.remarks].join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const sorted = status === "With Karigar"
    ? [...filtered].sort((a, b) => String(b.sent_date || "").localeCompare(String(a.sent_date || "")))
    : filtered;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length}{hasFilters ? ` of ${products?.length || 0}` : ""} products across all orders</p>
        </div>
        <Button variant="outline" size="sm" data-testid="karigar-report-button" className="rounded-full border-amber-500/40 text-amber-600 hover:text-amber-600" onClick={() => setReportOpen(true)}>
          <FileText className="h-3.5 w-3.5 mr-1.5" /> Generate Karigar Report
        </Button>
      </div>
      <KarigarReportDialog open={reportOpen} onOpenChange={setReportOpen} />

      <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="product-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input data-testid="product-search" className="pl-8 h-9" placeholder="Search product, order #, customer, phone..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={customer} onValueChange={setCustomer}>
            <SelectTrigger data-testid="product-filter-customer" className="w-[150px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["All", ...uniq("customer_name")].map((c) => <SelectItem key={c} value={c}>{c === "All" ? "All Customers" : c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={karigar} onValueChange={setKarigar}>
            <SelectTrigger data-testid="product-filter-karigar" className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["All", ...uniq("karigar_name")].map((k) => <SelectItem key={k} value={k}>{k === "All" ? "All Karigars" : k}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="product-filter-status" className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["All", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All Status" : s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="product-filter-category" className="w-[145px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["All", ...uniq("category")].map((c) => <SelectItem key={c} value={c}>{c === "All" ? "All Categories" : c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={delivery} onValueChange={setDelivery}>
            <SelectTrigger data-testid="product-filter-delivery" className="w-[150px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["all", "Any Delivery"], ["today", "Due Today"], ["tomorrow", "Due Tomorrow"], ["overdue", "Overdue"], ["custom", "Exact Date"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {delivery === "custom" && <DateField testId="product-filter-date" value={deliveryDate} onChange={setDeliveryDate} />}
          {hasFilters && (
            <Button variant="ghost" size="sm" data-testid="product-clear-filters" className="h-9 text-muted-foreground" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Package className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">{hasFilters ? "No products match your filters" : "No products yet"}</p>
          <p className="text-sm text-muted-foreground">Products added inside orders appear here automatically.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Karigar</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Karigar Cost</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Sent to Karigar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((p) => {
                const pr = priorityOf(p);
                return (
                  <TableRow key={`${p.order_id}-${p.id}`} data-testid={`product-row-${p.id}`} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/products/${p.order_id}/${p.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {p.photo ? (
                          <AuthImage src={`/orders/${p.order_id}/products/${p.id}/photo?v=${(p.photo.storage_path || "").slice(-8)}`} alt={p.name} className="h-8 w-8 rounded-lg object-cover border shrink-0" />
                        ) : (
                          <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                            <Package className="h-3.5 w-3.5 text-amber-500" />
                          </div>
                        )}
                        <span className="font-medium">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{p.category || "—"}</TableCell>
                    <TableCell className="font-medium">{p.order_number}</TableCell>
                    <TableCell>{p.customer_name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.customer_phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{p.karigar_name || "—"}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="text-right">{p.quantity}</TableCell>
                    <TableCell className="text-right">{fmt(p.customer_price)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(p.karigar_cost)}</TableCell>
                    <TableCell className={`text-right ${p.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(p.profit)}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(p.delivery_date)}</TableCell>
                    <TableCell><span className={pr.cls}>{pr.label}</span></TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(p.sent_date)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
